import { Link } from "raviger";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Avatar } from "@/components/Common/Avatar";
import { FacilityMapsLink } from "@/components/Facility/FacilityMapLink";

import { FeatureBadge } from "@/pages/Facility/Utils";
import { FacilityPublicRead, FacilityRead } from "@/types/facility/facility";
import { facilityTypeLabel } from "@/types/facility/setup-types";
import { useTranslation } from "react-i18next";

interface Props {
  facility: FacilityRead | FacilityPublicRead;
  className?: string;
}

export function FacilityCard({ facility, className }: Props) {
  const { t, i18n } = useTranslation();
  return (
    <Card className={cn("overflow-hidden bg-white", className)}>
      <div className="flex flex-col h-full">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="size-32 shrink-0 overflow-hidden rounded-lg">
              <Avatar
                imageUrl={facility.read_cover_image_url}
                name={facility.name || ""}
              />
            </div>

            <div className="flex grow flex-col min-w-0">
              <h3 className="truncate text-xl font-semibold">
                {facility.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {t("facility_type_value", {
                  label: facilityTypeLabel(
                    { text: facility.facility_type },
                    i18n.resolvedLanguage || i18n.language,
                  ),
                })}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {[facility.address].filter(Boolean).join(", ")}
                {facility.latitude && facility.longitude && (
                  <FacilityMapsLink
                    latitude={facility.latitude}
                    longitude={facility.longitude}
                  />
                )}
              </p>

              <div className="mt-2 flex flex-wrap gap-2">
                {facility.features?.map((featureId) => (
                  <FeatureBadge
                    key={featureId}
                    featureId={featureId as number}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto border-t border-gray-100 bg-gray-50 p-4">
          <div className="flex justify-end">
            <Button variant="outline" asChild>
              <Link href={`/facility/${facility.id}`}>
                {t("view_facility")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
